import cn from 'classnames';
import { Button, Dropdown, MenuProps, Tooltip } from 'antd';
import Assets from 'assets/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setDocsSegment, setIsOpenInfoEntityModal, setIsOpenMoveEntityModal, setIsOpenNewVersionModal, setIsOpenRenameEntityModal, setIsOpenShareEntityModal, setIsRSidebarCollapsed } from 'store/slices/windowStateSlice';
import { setIsOpenAddNoteModal } from 'store/slices/dataNotes';
import { typesToSigning } from 'helpers/filesExtensions';
import { faFeather } from '@fortawesome/free-solid-svg-icons';
import { IDocument, IFolder } from 'interfaces';
import { handleDownload } from 'store/reducers/documentsCreator';
import classes from './ControlGroups.module.scss';
import { setIsOpenAIAssistanceSider, setIsOpenAnalysisSider } from 'store/slices/aiFunctionsDataSlice';

// const isAvailableEditing = window.REACT_APP_IS_EDITING_ACTIVE !== 'REPLACE_REACT_APP_IS_EDITING_ACTIVE' ? window.REACT_APP_IS_EDITING_ACTIVE : process.env.REACT_APP_IS_EDITING_ACTIVE || 'false';

const ControlDocument = () => {
  const { rSidebarEntity, messageApi } = useAppSelector((state) => state.windowState);
  const { selectedDocument, currentFolder, filesForDownloading, selectedVersion } = useAppSelector((state) => state.documents);
  const { userFoldersPermissions, userRootPermissions } = useAppSelector((store) => store.permissions);
  const { userPermissions, userData } = useAppSelector((state) => state.userData);
  
  const tokensForDownloading = filesForDownloading.map((file) => file?.token || '');
  const isExperimentalAvailable = userData?.is_experimental_available;
  const isChoseOldVersion = Boolean(selectedVersion?.id);

  const isRootFolder = !currentFolder[0];
  const targetFolderPermissions =
    isRootFolder && userRootPermissions
      ? userRootPermissions.permissions
      : userFoldersPermissions?.find(
          (folder) => folder.id === currentFolder[0]?.id
        )?.permissions;

  const isAvailableUploadNewVersion = targetFolderPermissions?.includes('can_upload');
  // const isAvailableEditMode = selectedDocument?.permissions?.includes('can_edit_document') && isAvailableEditing === 'true';
  const isDeletedFile = selectedDocument?.deleted_at;

  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  
  const openRightSideBar = (section: string, width: number) => {
    !rSidebarEntity || section !== rSidebarEntity
      ? dispatch(
          setIsRSidebarCollapsed({
            collapsed: false,
            entity: section,
            width,
          })
        )
      : dispatch(
          setIsRSidebarCollapsed({
            collapsed: true,
            entity: '',
            width: 0,
          })
        );
  };

  const closeAITools = () => {
    dispatch(setIsOpenAnalysisSider({ open: false, width: 0 }));
    dispatch(setIsOpenAIAssistanceSider({ open: false, width: 0 }));
  };

  const toEditMode = () => {
    dispatch(setIsOpenAnalysisSider({ open: false, width: 0 }));
    isExperimentalAvailable && dispatch(setIsOpenAIAssistanceSider({ open: true, width: 400 }));
    dispatch(setDocsSegment('edit'));
  };

  const onSectionIconClick = (section: string) => {
    if (section) {
      openRightSideBar(section, 350);
      dispatch(setDocsSegment('view'));
    }
  };

  const handleOpenInfoFileModal = () => {
    dispatch(setIsOpenInfoEntityModal(true));
  };
  
  const addItemsFile: MenuProps['items'] = [];

  if (selectedDocument) {
    addItemsFile.push({
      label: (
        <span className={classes.spanItem}>
          <Assets.NoteIcon /> {t('Documents.details.notes.addNote')}
        </span>
      ),
      onClick: () => {
        dispatch(setDocsSegment('notes'));
        dispatch(setIsOpenAddNoteModal(true));
      },

      className: classes.menuItem,
      key: 'addNote',
    });

    Boolean(selectedDocument.signing_status === null) &&
      addItemsFile.push({
        label: (
          <span className={classes.spanItem}>
            <Assets.FileAddIcon /> {t('Documents.contextMenu.addVersions')}
          </span>
        ),
        onClick: () => {
          dispatch(setDocsSegment('versions'));
          dispatch(setIsOpenNewVersionModal(true));
        },
        className: classes.menuItem,
        disabled: !isAvailableUploadNewVersion,
        key: 'addVersions',
      });
  };

  const renderButton = () => {
    return (
      <Button
        loading={Boolean(tokensForDownloading[0])}
        className={classes.buttonWithIcon}
        type='text'
        icon={<Assets.VerticalIcon />}
      />
    );
  };

  const handleDownloadFile = async (entity: IDocument | IFolder, version: IDocument, isPdf?: boolean) => {
    dispatch(handleDownload({
      is_webview: !!isPdf,
      language: i18n.language,
      t,
      messageApi: messageApi!,
      targetDocument: { entity, version },
    }));
  };

  return (
    <div className={classes.iconsGroup}>
      { !isChoseOldVersion &&
        <>
          <Dropdown
            menu={{ items: addItemsFile }}
            trigger={['click']}
            disabled={Boolean(isDeletedFile) || Boolean(!selectedDocument)}
            classNames={{ root: classes.addMenu }}
          >
            <Button
              className={classes.plusButton}
              icon={<Assets.PlusIcon />}
              shape='circle'
              size='small'
              type='primary'
            />
          </Dropdown>

          {/* {isAvailableEditMode && editableExtensions.includes(selectedDocument?.extension?.toLocaleLowerCase()!) && selectedDocument?.signing_status === null &&
            <Tooltip title={t('Documents.details.edit')}>
              <Button
                className={cn(classes.settingButton, Boolean(isDeletedFile) ? classes.disable : 'null')}
                icon={<Assets.FileEditIcon />}
                type='text'
                disabled={Boolean(isDeletedFile)}
                onClick={toEditMode}
              />
            </Tooltip>
          } */}

          {userPermissions.can_manage_signing && typesToSigning.includes(selectedDocument?.extension?.toLocaleLowerCase()!)
            && <Tooltip title={t('Documents.table.columns.signing')}>
              <Button
                className={cn(classes.settingButton, Boolean(isDeletedFile) ? classes.disable : 'null')}
                icon={<FontAwesomeIcon icon={faFeather} />}
                type='text'
                disabled={Boolean(isDeletedFile) || Boolean(!selectedDocument)}
                onClick={() => {onSectionIconClick('file_setting.signing');closeAITools();}}
              />
            </Tooltip>
          }

          {userPermissions.can_edit_agreement && selectedDocument && <Tooltip title={t('Documents.table.columns.agreement')}>
            <Button
              className={cn(classes.settingButton, Boolean(isDeletedFile) ? classes.disable : 'null')}
              icon={<Assets.VerifyIcon />}
              type='text'
              disabled={Boolean(isDeletedFile)}
              onClick={() => {onSectionIconClick('file_setting.agreement');closeAITools();}}
            />
          </Tooltip>}

          {selectedDocument && (
            <ContextMenu
              key={selectedDocument?.key}
              entity={selectedDocument}
              t={t}
              renderFunc={() => renderButton()}
              moveModalOpenHandle={() => dispatch(setIsOpenMoveEntityModal(true))}
              renameModalHandle={() => dispatch(setIsOpenRenameEntityModal(true))}
              shareModalHandle={() => dispatch(setIsOpenShareEntityModal(true))}
              isDeletedEntity={Boolean(isDeletedFile)}
              type={'documentDetails'}
              onSectionIconClick={onSectionIconClick}
              onDownloadFile={handleDownloadFile}
              openInfoModal={handleOpenInfoFileModal}
            />
          )}
        </>
      }
    </div>
  );
}

export default ControlDocument;
